"use client"

import { Container, clx } from "@medusajs/ui"
import Image from "next/image"
import React, { useState } from "react"

import PlaceholderImage from "@modules/common/icons/placeholder-image"
import {
  CATALOG_GRID_PRODUCT_CARD_IMAGE_SIZES,
  SQUARE_THUMB_IMAGE_SIZES,
} from "@lib/util/storefront-image-sizes"
import { PRODUCT_CARD_IMAGE_QUALITY } from "@lib/util/storefront-image-quality"

type ThumbnailProps = {
  thumbnail?: string | null
  // TODO: Fix image typings
  images?: any[] | null
  videoUrl?: string | null
  posterUrl?: string | null
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  sizes?: string
  className?: string
  "data-testid"?: string
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  videoUrl,
  posterUrl,
  size = "small",
  isFeatured,
  sizes,
  className,
  "data-testid": dataTestid,
}) => {
  const resolveImageUrl = (candidate: any): string | undefined => {
    if (typeof candidate?.url === "string" && candidate.url.trim().length > 0) {
      return candidate.url.trim()
    }
    if (typeof candidate?.src === "string" && candidate.src.trim().length > 0) {
      return candidate.src.trim()
    }
    return undefined
  }

  const normalizedThumbnail =
    typeof thumbnail === "string" && thumbnail.trim().length > 0
      ? thumbnail.trim()
      : undefined
  const initialImage = normalizedThumbnail || resolveImageUrl(images?.[0])
  const hoverImage = resolveImageUrl(images?.[1])
  const normalizedVideoUrl =
    typeof videoUrl === "string" && videoUrl.trim().length > 0
      ? videoUrl.trim()
      : undefined
  const shouldShowVideo = Boolean(normalizedVideoUrl)
  const canHoverSwap =
    !shouldShowVideo &&
    typeof hoverImage === "string" &&
    hoverImage.length > 0 &&
    hoverImage !== initialImage

  return (
    <Container
      className={clx(
        "relative w-full overflow-hidden p-4 rounded-none shadow-none outline-none group",
        className,
        {
          "aspect-[11/14]": isFeatured,
          "aspect-[9/16]": !isFeatured && size !== "square",
          "aspect-[1/1]": size === "square",
          "w-[180px]": size === "small",
          "w-[290px]": size === "medium",
          "w-[440px]": size === "large",
          "w-full": size === "full",
        }
      )}
      data-testid={dataTestid}
    >
      <ImageOrPlaceholder
        image={initialImage}
        hoverImage={canHoverSwap ? hoverImage : undefined}
        videoUrl={shouldShowVideo ? normalizedVideoUrl : undefined}
        posterUrl={posterUrl ?? undefined}
        size={size}
        sizes={sizes}
      />
    </Container>
  )
}

const ImageOrPlaceholder = ({
  image,
  hoverImage,
  videoUrl,
  posterUrl,
  size,
  sizes,
}: Pick<ThumbnailProps, "size"> & {
  image?: string
  hoverImage?: string
  videoUrl?: string
  posterUrl?: string
  sizes?: string
}) => {
  const [failedImage, setFailedImage] = useState<string | null>(null)
  const [failedHoverImage, setFailedHoverImage] = useState<string | null>(null)
  const resolvedSizes =
    sizes ||
    (size === "square"
      ? SQUARE_THUMB_IMAGE_SIZES
      : CATALOG_GRID_PRODUCT_CARD_IMAGE_SIZES)

  // Prioritize video when it exists
  if (videoUrl) {
    return (
      <video
        autoPlay
        muted
        loop
        controlsList="nodownload"
        playsInline
        preload="metadata"
        className="video-item absolute inset-0 h-full w-full object-cover object-center"
        poster={posterUrl}
        src={videoUrl}
      >
        <source src={videoUrl} type="video/mp4" />
      </video>
    )
  }

  if (image && failedImage !== image) {
    return (
      <>
        <Image
          src={image}
          alt="Thumbnail"
          className={clx(
            "absolute inset-0 object-cover object-center rounded-none transition-opacity duration-500 ease-in-out",
            hoverImage ? "opacity-100 group-hover:opacity-0" : "opacity-100"
          )}
          draggable={false}
          quality={PRODUCT_CARD_IMAGE_QUALITY}
          sizes={resolvedSizes}
          fill
          onError={() => setFailedImage(image)}
        />
        {hoverImage && failedHoverImage !== hoverImage && (
          <Image
            src={hoverImage}
            alt="Thumbnail hover"
            className="absolute inset-0 object-cover object-center rounded-none opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100"
            draggable={false}
            quality={PRODUCT_CARD_IMAGE_QUALITY}
            sizes={resolvedSizes}
            fill
            loading="lazy"
            onError={() => setFailedHoverImage(hoverImage)}
          />
        )}
      </>
    )
  }

  return (
    <div className="w-full h-full absolute inset-0 flex items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}

export default Thumbnail
